import { useState } from "react";
import { X } from "lucide-react";

export default function DesignPartnerModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    numAgents: "",
    sector: "",
    useCase: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/design-partners`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitted(true);
        setTimeout(() => {
          onClose();
          setSubmitted(false);
          setFormData({ name: "", email: "", company: "", numAgents: "", sector: "", useCase: "" });
        }, 3000);
      } else {
        const data = await response.json();
        setError(data.detail || "Failed to submit application. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-void/80 backdrop-blur-sm">
      <div className="bg-surface border border-line-1 max-w-lg w-full mx-4 relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-ink-500 hover:text-ink-100"
        >
          <X size={20} />
        </button>

        {!submitted ? (
          <div className="p-8">
            <h2 className="text-2xl font-bold text-ink-100 mb-2">
              Become a Design Partner
            </h2>
            <p className="text-sm text-ink-500 mb-6">
              Shape the future of AI agent memory. Get early access, direct support, and influence our roadmap.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-900/20 border border-red-500 px-4 py-3 text-sm text-red-300">
                  {error}
                </div>
              )}
              
              <div>
                <label className="block text-xs text-ink-300 mb-1">
                  Your Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-void border border-line-2 px-4 py-2 text-ink-100 text-sm focus:border-cyber focus:outline-none"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-xs text-ink-300 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-void border border-line-2 px-4 py-2 text-ink-100 text-sm focus:border-cyber focus:outline-none"
                  placeholder="john@company.com"
                />
              </div>

              <div>
                <label className="block text-xs text-ink-300 mb-1">
                  Company *
                </label>
                <input
                  type="text"
                  required
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full bg-void border border-line-2 px-4 py-2 text-ink-100 text-sm focus:border-cyber focus:outline-none"
                  placeholder="Acme Inc"
                />
              </div>

              <div>
                <label className="block text-xs text-ink-300 mb-1">
                  Number of AI Agents *
                </label>
                <select
                  required
                  value={formData.numAgents}
                  onChange={(e) => setFormData({ ...formData, numAgents: e.target.value })}
                  className="w-full bg-void border border-line-2 px-4 py-2 text-ink-100 text-sm focus:border-cyber focus:outline-none"
                >
                  <option value="">Select...</option>
                  <option value="1-5">1-5 agents</option>
                  <option value="6-20">6-20 agents</option>
                  <option value="21-50">21-50 agents</option>
                  <option value="51+">51+ agents</option>
                </select>
              </div>

              <div>
                <label className="block text-xs text-ink-300 mb-1">
                  Industry/Sector *
                </label>
                <select
                  required
                  value={formData.sector}
                  onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                  className="w-full bg-void border border-line-2 px-4 py-2 text-ink-100 text-sm focus:border-cyber focus:outline-none"
                >
                  <option value="">Select...</option>
                  <option value="enterprise-software">Enterprise Software</option>
                  <option value="fintech">Fintech</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="ecommerce">E-commerce</option>
                  <option value="ai-ml">AI/ML</option>
                  <option value="devtools">Developer Tools</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-xs text-ink-300 mb-1">
                  Primary Use Case *
                </label>
                <textarea
                  required
                  value={formData.useCase}
                  onChange={(e) => setFormData({ ...formData, useCase: e.target.value })}
                  className="w-full bg-void border border-line-2 px-4 py-2 text-ink-100 text-sm focus:border-cyber focus:outline-none"
                  placeholder="Describe how you plan to use MnemonicAI..."
                  rows="3"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand text-void px-5 py-3 text-xs font-bold tracking-[0.2em] uppercase hover:shadow-[0_0_24px_rgba(0,229,255,0.45)] transition-shadow disabled:opacity-50"
              >
                {loading ? "Submitting..." : "Submit Application"}
              </button>
            </form>
          </div>
        ) : (
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 border border-terminal flex items-center justify-center">
              <svg className="w-8 h-8 text-terminal" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-ink-100 mb-2">
              Application Received!
            </h3>
            <p className="text-sm text-ink-500">
              We'll be in touch within 48 hours. Check your email for next steps.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
